import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';

const RoleCreationModal = ({ isOpen, onClose, onCreateRole }) => {
  const { t } = useTranslation('rbac');
  const [formData, setFormData] = useState({
    roleKey: '',
    name: '',
    description: '',
    level: 50
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.roleKey.trim()) {
      newErrors.roleKey = t('roleKeyRequired');
    } else if (!/^[a-z_][a-z0-9_]*$/.test(formData.roleKey)) {
      newErrors.roleKey = t('roleKeyFormat');
    }
    
    if (!formData.name.trim()) {
      newErrors.name = t('roleNameRequired');
    }
    
    if (!formData.description.trim()) {
      newErrors.description = t('descriptionRequired');
    }
    
    if (formData.level < 1 || formData.level > 100) {
      newErrors.level = t('levelRange');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onCreateRole(formData);
      // Reset form
      setFormData({
        roleKey: '',
        name: '',
        description: '',
        level: 50
      });
      setErrors({});
      onClose();
    }
  };

  const handleCancel = () => {
    setFormData({
      roleKey: '',
      name: '',
      description: '',
      level: 50
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('createCustomRole')}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="modal-form-group">
          <label htmlFor="roleKey">{t('roleKeyLabel')}</label>
          <input
            id="roleKey"
            type="text"
            value={formData.roleKey}
            onChange={(e) => handleInputChange('roleKey', e.target.value)}
            placeholder={t('roleKeyPlaceholder')}
            className={errors.roleKey ? 'error' : ''}
          />
          {errors.roleKey && <span className="error-message">{errors.roleKey}</span>}
        </div>

        <div className="modal-form-group">
          <label htmlFor="name">{t('roleNameLabel')}</label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder={t('roleNamePlaceholder')}
            className={errors.name ? 'error' : ''}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className="modal-form-group">
          <label htmlFor="description">{t('descriptionLabel')}</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder={t('descriptionPlaceholder')}
            rows={3}
            className={errors.description ? 'error' : ''}
          />
          {errors.description && <span className="error-message">{errors.description}</span>}
        </div>

        <div className="modal-form-group">
          <label htmlFor="level">{t('roleLevelLabel')}</label>
          <input
            id="level"
            type="number"
            min="1"
            max="100"
            value={formData.level}
            onChange={(e) => handleInputChange('level', parseInt(e.target.value) || 50)}
            className={errors.level ? 'error' : ''}
          />
          {errors.level && <span className="error-message">{errors.level}</span>}
          <small className="form-help">{t('levelHelp')}</small>
        </div>

        <div className="modal-form-actions">
          <button type="button" className="modal-btn modal-btn-secondary" onClick={handleCancel}>
            {t('cancel')}
          </button>
          <button type="submit" className="modal-btn modal-btn-primary">
            {t('createRoleButton')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default RoleCreationModal;

